import { Popover as BasePopover } from '@base-ui/react/popover'
import type { ReactElement, ReactNode, RefObject } from 'react'
import { cn } from '../lib/cn'
import { LayerProvider, useLayer, Z } from './context'

export interface PopoverProps {
  /** Element, das die Ebene öffnet (nimmt Ref und Ereignisse an). Ohne Auslöser: `anchor` + `open` verwenden. */
  trigger?: ReactElement<Record<string, unknown>>
  /** Anker statt Auslöser – für Felder, die ihre Liste selbst steuern (Combobox, DatePicker) */
  anchor?: RefObject<Element | null>
  open?: boolean
  onOpenChange?: (open: boolean, details: { reason: string; target: EventTarget | null }) => void
  /** öffnet zusätzlich beim Überfahren des Auslösers (kurze Hinweise) – Klick, Tippen und Tastatur bleiben */
  openOnHover?: boolean
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  /** zugänglicher Name, wenn es keinen sichtbaren Titel gibt */
  ariaLabel?: string
  title?: string
  /** Breite: 'anchor' = so breit wie der Anker */
  width?: 'auto' | 'anchor' | 'sm' | 'md' | 'lg'
  /** Innenabstand; Listen und Kalender bringen ihren eigenen mit */
  padding?: boolean
  /** false: Fokus bleibt, wo er ist (Listen unter einem Eingabefeld) */
  focusOnOpen?: boolean
  /** true: Fokus bleibt gefangen und die Seite dahinter ist gesperrt */
  modal?: boolean
  className?: string
  children: ReactNode | ((ctx: { close: () => void }) => ReactNode)
}

const WIDTH: Record<NonNullable<PopoverProps['width']>, string> = {
  auto: 'max-w-(--available-width)',
  anchor: 'w-(--anchor-width) min-w-48',
  sm: 'w-64',
  md: 'w-80',
  lg: 'w-112',
}

export const overlayClass = cn(
  'rounded-md border border-line bg-overlay text-fg shadow-md outline-none',
  'origin-(--transform-origin) transition-[opacity,scale,translate] duration-(--duration-base) ease-out',
  'data-starting-style:translate-y-1 data-starting-style:scale-98 data-starting-style:opacity-0',
  'data-ending-style:opacity-0 data-ending-style:duration-(--duration-fast) data-ending-style:ease-in',
)

/** Schwebende Ebene an einem Auslöser oder Anker: Filterwert, Kalender, Trefferliste. */
export function Popover({
  trigger,
  anchor,
  open,
  onOpenChange,
  openOnHover = false,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  ariaLabel,
  title,
  width = 'auto',
  padding = true,
  focusOnOpen = true,
  modal = false,
  className,
  children,
}: PopoverProps) {
  const zIndex = useLayer(Z.popover)
  const close = (): void => onOpenChange?.(false, { reason: 'imperative-action', target: null })
  return (
    <BasePopover.Root
      open={open}
      modal={modal}
      onOpenChange={(next, details) =>
        onOpenChange?.(next, { reason: details.reason, target: details.event?.target ?? null })
      }
    >
      {trigger && <BasePopover.Trigger render={trigger} openOnHover={openOnHover} delay={250} />}
      <BasePopover.Portal>
        <BasePopover.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          anchor={anchor}
          collisionPadding={8}
          style={{ zIndex }}
        >
          <BasePopover.Popup
            aria-label={title ? undefined : ariaLabel}
            initialFocus={focusOnOpen ? undefined : false}
            finalFocus={focusOnOpen ? undefined : false}
            className={cn(
              overlayClass,
              WIDTH[width],
              'max-h-(--available-height) overflow-auto',
              padding && 'p-3',
              className,
            )}
          >
            <LayerProvider z={zIndex}>
              {title && <BasePopover.Title className="mb-2 text-dense font-semibold">{title}</BasePopover.Title>}
              {typeof children === 'function' ? children({ close }) : children}
            </LayerProvider>
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  )
}
